// The rail: the mark and name, workspace tiles, practice rows, and the foot
// with the firm's logo (or the attorney's initials) beside the attorney.

import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { OTHER_AREA, areasWithMatters } from "../../data/practice-areas";
import { initialsOf } from "../../api/auth";
import { firmLogoSrc, moduleOn, siteHostOf, useFirmIdentity } from "../../api/attorneyConfig";
import { useCurrentUser } from "../auth/CurrentUserContext";
import { isAdminRole } from "../../lib/roles";
import { useFirmCounts } from "../../hooks/useFirmCounts";
import { fmtNum } from "../../lib/money";
import { Mark } from "./Mark";
import { SearchTrigger } from "./SearchTrigger";
import { useScreens } from "./screens";

// U+2699 gear with the text-presentation selector; built from char codes
// because source files stay keyboard-ASCII.
const GEAR_ICON = String.fromCharCode(9881, 65038);
// U+2193 down arrow, the inbox tile's icon, for the same reason.
const INBOX_ICON = String.fromCharCode(8595);

interface Props {
  onOpenSearch: () => void;
}

interface RailItem {
  id: string;
  icon: string;
  label: string;
  badge?: string;
  adminOnly?: boolean;
  active?: boolean;
  /** A module tile: where it goes, which is also what it highlights for. */
  to?: string;
}

export function NavRail({ onOpenSearch }: Props) {
  const navigate = useNavigate();
  const location = useLocation();
  // Badge counts come from the firm aggregate, not from a list fetch: the
  // rail renders on every screen and a firm can hold thousands of matters.
  // While the aggregate is loading or failed there is NO badge - an
  // invented number is worse than none.
  const counts = useFirmCounts();
  const isAdmin = isAdminRole(useCurrentUser()?.role);
  // Firm identity comes from the real config, never the fixture: while it
  // is loading or unset the chrome stays silent instead of inventing a
  // name or a bar number.
  const identity = useFirmIdentity().data;
  const screens = useScreens();
  const siteHost = siteHostOf(identity?.website);
  const attorney = identity?.attorneyFullName ?? "";
  // The number is the substance - a bare state ("PA") is noise, not a bar.
  const bar = identity?.barNumber
    ? [identity.barState, identity.barNumber].filter(Boolean).join(" ")
    : "";
  // A logo the server no longer has (a cleared upload) must not leave a
  // broken image in the foot.
  const logoSrc = firmLogoSrc(identity?.logoVersion);
  const [brokenLogo, setBrokenLogo] = useState<string | null>(null);
  const logo = logoSrc && logoSrc !== brokenLogo ? logoSrc : null;

  // View kind from current path. The route table is the single source of
  // truth; we only need to know what's "active" for highlight purposes.
  const path = location.pathname;
  const isFirm = path === "/" || path === "";
  const isTeam = path === "/team" || path.startsWith("/team/");
  const practiceMatch = path.match(/^\/practice\/(.+)$/);
  const activeArea = practiceMatch ? practiceMatch[1] : null;

  const workspaceItems: RailItem[] = [
    { id: "cmd", icon: "▸", label: "Command Center", active: isFirm },
    { id: "cases", icon: "▢", label: "Cases", badge: counts && fmtNum(counts.activeMatters) },
    // Interview-born matters nobody vetted yet. Self-hiding: an empty
    // review queue is the normal state, not a zero to display.
    ...(counts && counts.needsReview > 0
      ? [{ id: "review", icon: "!", label: "Review", badge: fmtNum(counts.needsReview) }]
      : []),
    { id: "clients", icon: "◯", label: "Clients", badge: counts && fmtNum(counts.clients) },
    // The unmatched queue is the admin's (the server refuses everyone else).
    {
      id: "inbox",
      icon: INBOX_ICON,
      label: "Inbox",
      badge: counts && fmtNum(counts.unmatched),
      adminOnly: true,
    },
    { id: "calendar", icon: "▤", label: "Calendar" },
    { id: "filings", icon: "✎", label: "Filings", badge: counts && fmtNum(counts.readyToFile) },
    { id: "team", icon: "◉", label: "Team", active: isTeam },
    // Module tiles sit together before Settings. A switched-off module
    // keeps its tile, marked: its records stay readable.
    ...screens.flatMap((s) =>
      (s.tiles ?? []).map((t) => ({
        ...t,
        badge: moduleOn(identity?.modules, s.id) ? undefined : "off",
      })),
    ),
    // Firm settings write the identity that prints on legal forms - the
    // tile exists only for the role that can use it.
    { id: "settings", icon: GEAR_ICON, label: "Settings", adminOnly: true },
  ].filter((it) => isAdmin || !it.adminOnly);

  const paCounts = counts?.byPracticeArea ?? {};
  const areas = areasWithMatters(paCounts);
  const otherCount = paCounts.other ?? 0;
  // The Other bucket has no /practice page; its row owns the highlight on
  // the index it filters while it is rendered, the same way the Review
  // tile owns the queue - an empty bucket hands the page back to Cases.
  const inOtherArea =
    otherCount > 0 &&
    path.startsWith("/cases") &&
    new URLSearchParams(location.search).get("area") === "other";

  return (
    <aside className="cc-nav">
      <div className="cc-nav-head" onClick={() => navigate("/")} style={{ cursor: "pointer" }}>
        <Mark size={32} />
        <div>
          <div className="cc-nav-name">DuraLex</div>
          {siteHost && <div className="cc-nav-firm">{siteHost}</div>}
        </div>
      </div>

      <SearchTrigger onOpen={onOpenSearch} full />

      <div className="cc-nav-section">Workspace</div>
      <div className="cc-nav-list">
        {workspaceItems.map((it) => {
          const onClick = () => {
            if (it.to) {
              navigate(it.to);
              return;
            }
            switch (it.id) {
              case "cmd":
                navigate("/");
                return;
              case "cases":
                navigate("/cases");
                return;
              case "review":
                navigate("/cases?review=1");
                return;
              case "clients":
                navigate("/clients");
                return;
              case "inbox":
                navigate("/inbox");
                return;
              case "calendar":
                navigate("/calendar");
                return;
              case "filings":
                navigate("/filings");
                return;
              case "team":
                navigate("/team");
                return;
              case "settings":
                navigate("/settings/firm");
                return;
              default:
                return;
            }
          };
          // Highlight the active tile based on the current path so the rail
          // mirrors where the user actually is. We can't depend on `it.active`
          // for the new routes because the workspaceItems array doesn't recompute
          // per render; derive it from the path here.
          // Same parse as CasesIndex; and the Review tile only owns the
          // highlight while it is rendered - once the queue empties, the
          // page belongs to Cases again.
          const inReviewQueue =
            path.startsWith("/cases") &&
            new URLSearchParams(location.search).get("review") === "1" &&
            !!counts &&
            counts.needsReview > 0;
          const activeForRoute = (() => {
            if (it.active) return true;
            if (it.id === "review" && inReviewQueue) return true;
            if (it.to && path.startsWith(it.to)) return true;
            if (it.id === "cases" && path.startsWith("/cases") && !inReviewQueue && !inOtherArea)
              return true;
            if (it.id === "clients" && path.startsWith("/clients")) return true;
            if (it.id === "inbox" && path.startsWith("/inbox")) return true;
            if (it.id === "calendar" && path.startsWith("/calendar")) return true;
            if (it.id === "filings" && path.startsWith("/filings")) return true;
            if (it.id === "settings" && path.startsWith("/settings")) return true;
            return false;
          })();
          return (
            <div
              key={it.id}
              className={"cc-nav-item " + (activeForRoute ? "active" : "")}
              onClick={onClick}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onClick();
                }
              }}
            >
              <span className="ic">{it.icon}</span>
              <span>{it.label}</span>
              {it.badge && <span className="badge">{it.badge}</span>}
            </div>
          );
        })}
      </div>

      {(areas.length > 0 || otherCount > 0) && (
        <>
          <div className="cc-nav-section">
            Practice - {areas.length + (otherCount > 0 ? 1 : 0)} area
            {areas.length + (otherCount > 0 ? 1 : 0) === 1 ? "" : "s"}
          </div>
          <div className="cc-nav-list">
            {areas.map((p) => {
              const isActive = activeArea === p.id;
              return (
                <div
                  key={p.id}
                  className={"cc-nav-item " + (isActive ? "active" : "")}
                  data-pa-tier={p.tier}
                  onClick={() => navigate(`/practice/${p.id}`)}
                  title={p.tagline}
                >
                  <span className="pa-dot" style={{ background: p.accent }} />
                  <span>{p.label}</span>
                  <span className="badge">{paCounts[p.id] || 0}</span>
                </div>
              );
            })}
            {otherCount > 0 && (
              <div
                className={"cc-nav-item " + (inOtherArea ? "active" : "")}
                onClick={() => navigate(OTHER_AREA.to)}
                title="Matters outside the configured practice areas"
              >
                <span className="pa-dot" style={{ background: OTHER_AREA.accent }} />
                <span>{OTHER_AREA.label}</span>
                <span className="badge">{otherCount}</span>
              </div>
            )}
          </div>
        </>
      )}

      {attorney && (
        <div className="cc-nav-foot">
          {logo ? (
            <img
              className="logo"
              src={logo}
              alt={identity?.firmName ?? ""}
              onError={() => setBrokenLogo(logo)}
            />
          ) : (
            <div className="av">{initialsOf(attorney)}</div>
          )}
          <div style={{ minWidth: 0, flex: 1 }}>
            <div className="who">{attorney}</div>
            {bar && <div className="role">{bar}</div>}
          </div>
        </div>
      )}
    </aside>
  );
}
